import { Head, router, usePage } from "@inertiajs/react";
import { useModalStack } from "@inertiaui/modal-react";
import { t } from "@travily/i18n";
import {
  newProductPurchasePath,
  newProductReviewPath,
  productEditorPath,
  productPath,
  productPurchasesPath,
  purchaseDownloadPath,
  rootPath,
} from "@travily/routes";
import type ProductsShow from "@travily/types/pages/ProductsShow";
import { HeartToggleButton } from "@ui/components/HeartToggleButton";
import { Icon } from "@ui/components/Icon";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useShare } from "@ui/hooks/use-share";
import { Button } from "@ui/primitives/Button";
import { ShowView } from "@/components/ProductShowView/ShowView";
import { useFavorite } from "@/hooks/use-favorite";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import { buildShowViewProps } from "@/lib/build-show-view-props";
import { wrapReviewsInScroll } from "@/lib/reviews-scroll";
import { useListingAnalytics } from "@/lib/use-listing-analytics";
import type { SharedProps } from "@/types";

const ProductShowPage = ({ product, creator, reviews, can, existing_purchase }: ProductsShow) => {
  const { current_user } = usePage<SharedProps>().props;
  const { trackCheckout } = useListingAnalytics(product, "product", creator.id);
  const isAuthenticated = Boolean(current_user);
  const isGuestCheckout = !isAuthenticated && product.can.purchase;
  const downloadHref =
    product.can.download && existing_purchase ? purchaseDownloadPath(existing_purchase.sqid) : undefined;
  const manageHref = product.can.manage ? productEditorPath(product.sqid) : undefined;

  const displayTitle = product.title ?? "";
  const { share, shared } = useShare();
  const { visitModal } = useModalStack();
  const onBack = useNavigateBack(rootPath());
  const favorite = useFavorite({
    favorited: product.favorited,
    favorableType: "Listing",
    favorableId: product.sqid,
  });

  const handleShare = () => {
    share({
      title: displayTitle,
      text: t("web.products.show.share_text", { title: displayTitle }),
      url: `${location.origin}${productPath(product.sqid)}`,
    });
  };

  const handlePurchase = () => {
    trackCheckout();
    if (isGuestCheckout) {
      visitModal(newProductPurchasePath(product.sqid));
      return;
    }
    router.post(productPurchasesPath(product.sqid));
  };

  const showViewProps = buildShowViewProps("product", product, creator, reviews);

  return (
    <PageFrame
      stickyTopBar={false}
      mobileTopBar={
        <TopBarMobile
          onBack={onBack}
          title={displayTitle}
          actions={
            <>
              <HeartToggleButton
                aria-label={t("common.actions.favorite")}
                isSelected={favorite.isFavorited}
                onChange={favorite.toggle}
                size="24"
              />
              <Button aria-label={t("common.actions.share")} onPress={handleShare}>
                <Icon name="share" size="20" type="sticker" />
              </Button>
            </>
          }
        />
      }
    >
      <Head title={t("web.products.show.meta_title", { title: displayTitle })}>
        <meta name="description" content={t("web.products.show.meta_description", { title: displayTitle })} />
      </Head>
      <ShowView
        {...showViewProps}
        downloadHref={downloadHref}
        favorited={favorite.isFavorited}
        manageHref={manageHref}
        onAddReview={can.create_review ? () => visitModal(newProductReviewPath(product.sqid)) : undefined}
        onFavoriteToggle={favorite.toggle}
        onPurchase={product.can.purchase ? handlePurchase : undefined}
        onShare={handleShare}
        shared={shared}
        reviewsListWrapper={wrapReviewsInScroll}
      />
    </PageFrame>
  );
};

export default ProductShowPage;
